import type { Component } from "vue";
import { UnitFamily } from "@shared/utils/myJFrogUtils.ts";
import { DateRangeType } from "@shared/components/date-range-picker/helper.ts";
import { GroupByOptionItem } from "@shared/components/usage-explorer/group-by-picker/helper.ts";
import { ChartID } from "@shared/types/localTypes/charts.ts";
import {
  CalculationMode,
  GranularityType,
} from "@shared/types/localTypes/common.ts";
import { UsageExplorerGenericTabState } from "../../../web/src/views/usage-explorer/tabs/generic/types";

export enum UsageExplorerTabNames {
  "dropdown" = "dropdown",
  "jas" = "jas",
  "curation" = "curation",
  "projects" = "projects",
  "dropdown_ml" = "dropdown_ml",
  "dropdown_archive" = "dropdown_archive",
}
export enum TrafficDropdownItemsTags {
  "data_transfer" = "data_transfer",
  "storage" = "storage",
  "consumption" = "consumption",
}
export enum ArchiveDropdownItemsTags {
  "archive_consumption" = "archive_consumption",
  "archive_data_transfer" = "archive_data_transfer",
  "archive_storage" = "archive_storage",
}
export enum MLDropdownItemsTags {
  "ml_credits" = "ml_credits",
  "ml_cache" = "ml_cache",
}
export type DropdownItemsTags =
  | keyof typeof TrafficDropdownItemsTags
  | keyof typeof MLDropdownItemsTags
  | keyof typeof ArchiveDropdownItemsTags;

export type UsageTypeConfig = {
  usageType: UsageType;
  sectionTitle: string;
  mainChartLabel: (state: UsageExplorerGenericTabState) => string;
  unitFamily: UnitFamily;
  hasExportCsv: boolean;
  groupByTypes: UsageGroupByType[];
  groupByToIgnoreFromServer: UsageGroupByType[];
  rangeType: DateRangeType;
  calculationModes: {
    tooltip: (state: UsageExplorerGenericTabState) => string | null;
    modes: CalculationMode[];
    default: CalculationMode;
    pickerLabel?: string;
  };
  valueFormatters: {
    full: (value: number) => string;
    shortened: (value: number) => string;
    chartTick: (value: number) => string;
  };
  defaultAdditionalGroupByOptions1: GroupByOptionItem;
  defaultAdditionalGroupByOptions2: GroupByOptionItem | null;
  defaultAdditionalGroupByOptions3: GroupByOptionItem | null;
  emptyState?: {
    display: boolean;
    component: Component;
  };
  additionalCharts: {
    display: (state: UsageExplorerGenericTabState) => boolean;
  };
  chart: {
    chartId: ChartID;
    scaleY: {
      title: string;
      addHighestSymbol: boolean;
    };
    tooltip: {
      itemSuffix: string;
    };
  };
  dataLoader: {
    groupByReferenceForComputation: UsageGroupByType;
  };
};

export interface DropDownItem {
  command: DropdownItemsTags;
  label: string;
  component: Component;
  usageType?: UsageType;
}

export enum ChartLabels {
  CURATION_APPROVED = "Approved",
  CURATION_BLOCKED = "Blocked",
  SCANS = "Scans",
  ML_CREDITS = "Credits",
  PROJECTS = "Projects",
  DATA_TRANSFER = "Data Transfer",
  OTHER = "Other",
  DATA_STORAGE = "Data Storage",
  CONSUMPTION_USAGE = "Consumption",
}

export enum UsageType {
  TRAFFIC = "traffic",
  STORAGE = "storage",
  JAS = "jas",
  CURATION = "curation",
  PROJECTS = "projects",
  ML_CREDITS = "ml-credits",
  ML_CACHE = "ml-cache",
  ARCHIVE_TRAFFIC = "archive-traffic",
  ARCHIVE_STORAGE = "archive-storage",
}

export enum TooltipLocation {
  DONUT_CHART = "donut-chart",
  EXPORT_CSV_FORM = "export-csv-form",
}

export interface ChartLabelData {
  label: string;
  displayedLabel: string;
  colorCode: string;
  canBeFiltered: boolean;
  displayInLegend: boolean;
  jpd?: string;
  displayedJpd?: string;
}

export enum UsageGroupByType {
  NONE = "none",
  PROJECT = "project",
  SERVICE = "service",
  USERNAME = "username",
  TECHNICAL_SERVER_NAME = "technical_server_name",
  JPD_ID = "jpd_id",
  ACTION = "action",
  REPOSITORY = "repository",
  REPOSITORY_TYPE = "repo_type",
  PACKAGE_TYPE = "package_type",
  VERDICT = "verdict",
  ARTIFACT_PATH = "artifact_path",
  IP = "ip",
  ENVIRONMENT_NAME = "environment_name",
  RESOURCE_TYPE = "resource_type",
  RESOURCE_NAME = "resource_name",
  PROJECT_KEY = "project_key",
  CREDIT_TYPE = "credit_type",
}

export type UsageExplorerTopItem = (string | number)[];

export interface UsageExplorerTopItemAccessors {
  readName: (item: UsageExplorerTopItem) => string;
  readValue: (item: UsageExplorerTopItem) => number;
  readJpd: (item: UsageExplorerTopItem) => string | undefined;
  readValueAt?: (item: UsageExplorerTopItem, index: number) => number;
}

export interface EntirePeriodGroupByResult {
  top: UsageExplorerTopItem[];
  remaining: number[] | null;
}

export interface PartitionGroupByResult extends EntirePeriodGroupByResult {
  date: string;
  value?: number;
}

export interface FetchUsageResponse {
  nonce: string;
  partition_group_by_result: Partial<
    Record<UsageGroupByType, PartitionGroupByResult[]>
  >;
  entire_period_group_by_result: Partial<
    Record<UsageGroupByType, EntirePeriodGroupByResult>
  >;
}

export interface AdditionalChartDataStore {
  readonly isLoading: boolean;
  readonly total: number;
  readonly data: FetchUsageResponse;
}

// mirrors usage-exposer constants.usageExplorerConsumptionStorageTypes
export const CONSUMPTION_STORAGE_TYPES = [
  "binary",
  "artifact",
  "normalized",
] as const;
export type ConsumptionStorageType = (typeof CONSUMPTION_STORAGE_TYPES)[number];

export type UsageExplorerFeatureFlagName = "storage_breakdown";

export interface UsageExplorerFeatureFlagsResponse {
  feature_flags: string[];
}

export interface UsageExplorerConsumptionState {
  startDate: number;
  endDate: number;
  granularity: GranularityType;
  filterOpened: boolean;
}
